import { useEffect, useRef, type ReactNode } from 'react';

// Generic modal shell built on the app's existing .modal-overlay/.modal-container
// styling (see styles.css "Confirmation Modal" — previously only used inline on
// VideoPage/SubmissionPage; this is the first shared, reusable wrapper around it).
export function Modal({
  title,
  onClose,
  children,
  footer,
  size = 'default',
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** `wide` for content that is a table rather than a form (e.g. a video's quality
      breakdown); every existing modal keeps the default width. */
  size?: 'default' | 'wide';
}) {
  // Callers pass a fresh inline onClose every render, so keeping it in a ref
  // (rather than the effect's dependency array) avoids tearing down and re-adding
  // the global keydown listener on every parent re-render while the modal is open.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={size === 'wide' ? 'modal-container modal-container--wide' : 'modal-container'}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="modal-header modal-header--with-close">
          <h3>{title}</h3>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-footer">{footer}</div> : null}
      </div>
    </div>
  );
}

export default Modal;
